import {
  RichTextarea,
  RichTextareaHandle,
} from "@jugl-web/domain-resources/common/components/RichTextarea";
import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { WhatsAppTemplateEditorToolbar } from "@jugl-web/domain-resources/integrations/components/WhatsAppTemplateEditorToolbar";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import { useTaskBoardsQuery } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardsQuery";
import { useTaskStatuses } from "@jugl-web/domain-resources/tasks/hooks/useTaskStatuses";
import { ParsedApiError, useRestApiProvider } from "@jugl-web/rest-api";
import {
  WhatsappTemplateCategory,
  WhatsappTemplatePayload,
  WhatsappTemplateValues,
} from "@jugl-web/rest-api/integrations/types";
import { TaskDefaultStatusId } from "@jugl-web/rest-api/tasks";
import { TaskBoardModel } from "@jugl-web/rest-api/tasks/models/TaskBoard";
import { TaskCustomStatusDto } from "@jugl-web/rest-api/tasks/models/TaskCustomStatus";
import {
  Alert,
  Button,
  DataLoadingWrapper,
  DotIcon,
  FormGroup,
  ListBoxItem,
  PlainButton,
  ResourcePickerPopover,
  SelectTrigger,
  TextInput,
} from "@jugl-web/ui-kit";
import { BoardAvatar } from "@jugl-web/domain-resources/tasks";
import {
  assignRefs,
  cx,
  isWhatsappApiError,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import {
  UPDATES_AUTOMATION_DIALOG_SHOWN,
  getStorageItemWithFallback,
  saveItemToStorage,
} from "@jugl-web/utils/storage";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { collapsedIconButtonClassName } from "@web-src/modules/client-support/pages/ClientSupportPage/consts";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useSupportedWhatsappVariables } from "@jugl-web/domain-resources/integrations";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import { rawBodyToWhatsappBody } from "../../utils/rawBodyToWhatsappBody";
import { whatsappBodyToRawBody } from "../../utils/whatsappBodyToRawBody";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import alertAnimation from "./assets/alert-animation.json";
import { AutomationUpdatesDialog } from "./components/AutomationUpdatesDialog";
import { TemplatePreview } from "./components/TemplatePreview";
import { getWhatsappTemplateComponentsList } from "./utils/getWhatsappTemplateComponentsList";
import { ManageAccountsDrawerScreenToParametersMap } from "../../types";
import { WhatsappAccountTab } from "../WhatsappAccountScreen/types";

export const WhatsappTemplateFormScreen: React.FC<{
  templateCategory: WhatsappTemplateCategory;
  templateId?: string;
}> = ({ templateCategory, templateId }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const {
    registerDirtySource,
    requestNavigation,
    reset: resetNavigationGuard,
  } = useNavigationGuardContext();
  const richTextareaRef = useRef<RichTextareaHandle | null>(null);

  const { entityId } = useEntitySelectedProvider();
  const { id: meId } = useMe();
  const { integrationsApi } = useRestApiProvider();
  const isMessageTemplate =
    templateCategory === WhatsappTemplateCategory.message;

  const [createWhatsappTemplate, { isLoading }] =
    integrationsApi.useCreateWhatsappTemplateMutation();
  const [
    updateWhatsappTemplate,
    { isLoading: isUpdateWhatsappTemplateLoading },
  ] = integrationsApi.useUpdateWhatsappTemplateMutation();
  const [isAutomationUpdatesDialogOpen, setIsAutomationUpdatesDialogOpen] =
    useState(
      !isMessageTemplate &&
        !getStorageItemWithFallback(UPDATES_AUTOMATION_DIALOG_SHOWN, false)
    );
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const {
    data: whatsappTemplate,
    isLoading: isWhatsappTemplateLoading,
    isError: isWhatsappTemplateError,
    isFetching: isWhatsappTemplateFetching,
    refetch: refetchWhatsappTemplate,
  } = integrationsApi.useGetWhatsappTemplateQuery(
    templateId
      ? {
          entityId,
          templateId,
        }
      : skipToken
  );

  const handleBack = useCallback(() => {
    transitionTo({
      name: "whatsappAccount",
      tab: isMessageTemplate
        ? WhatsappAccountTab.messageTemplates
        : WhatsappAccountTab.taskTemplates,
    });
  }, [isMessageTemplate, transitionTo]);

  const handleSafeBack = useCallback(() => {
    requestNavigation(handleBack);
  }, [handleBack, requestNavigation]);

  const handleCreateTemplate = async ({
    name,
    footer,
    board_id,
    action,
    header,
    bodyPlain,
  }: WhatsappTemplateValues) => {
    if (!richTextareaRef.current) {
      return;
    }

    const whatsappBody = rawBodyToWhatsappBody(bodyPlain, supportedVariables);

    const data: WhatsappTemplatePayload = {
      name,
      board_id,
      action,
      type: templateCategory as WhatsappTemplateCategory,
      template: {
        components: getWhatsappTemplateComponentsList({
          footer,
          header,
          body: whatsappBody.body,
          variables: whatsappBody.variables,
        }),
      },
    };

    const meta = {
      successMessage: templateId
        ? t({
            id: "client-support-page-page.whatsapp-template-was-updated-and-sent-to-approval",
            defaultMessage:
              "WhatsApp template was updated and sent to Approval",
          })
        : t({
            id: "client-support-page-page.whatsapp-template-was-created-and-sent-to-approval",
            defaultMessage:
              "WhatsApp template was created and sent to Approval",
          }),
      onError: (error: ParsedApiError) => {
        if (!isWhatsappApiError(error.raw)) {
          return "unhandled";
        }
        if ("error" in error.raw.data) {
          toast(error.raw.data.error.error_user_msg, { variant: "error" });
          return undefined;
        }
        const errorObj = error.raw.data.errors?.[0];
        if (errorObj) {
          if (errorObj.field === "entity_id") {
            setIsAlertOpen(true);
            return undefined;
          }
          toast(errorObj.message, { variant: "error" });
          return undefined;
        }
        return "unhandled";
      },
    };

    const response = templateId
      ? await updateWhatsappTemplate({
          entityId,
          data,
          templateId,
          meta,
        })
      : await createWhatsappTemplate({
          entityId,
          data,
          meta,
        });

    if (response && "data" in response && templateCategory) {
      resetNavigationGuard();
      handleBack();
    }
  };

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty, errors, isSubmitted },
  } = useForm<WhatsappTemplateValues>({
    mode: "onSubmit",
    defaultValues: {
      bodyPlain: "",
      bodyHtml: "",
      footer: "",
      header: "",
      name: "",
      board_id: undefined,
      action:
        templateCategory === WhatsappTemplateCategory.tasks
          ? TaskDefaultStatusId.pending
          : undefined,
    },
  });

  const bodyPlain = watch("bodyPlain");
  const bodyHtml = watch("bodyHtml");
  const header = watch("header");
  const footer = watch("footer");
  const boardId = watch("board_id");
  const statusId = watch("action");

  const { variables: supportedVariables } = useSupportedWhatsappVariables(
    entityId,
    boardId
  );

  const { getBoardByIdFromCache } = useTaskBoardsQuery({ entityId });

  const { accessibleBoards } = useTaskBoardList({
    entityId,
    meId: meId || "",
  });

  const boardsAsListItems = useMemo<ListBoxItem<TaskBoardModel>[]>(
    () => accessibleBoards.map((b) => ({ id: b.id, value: b })),
    [accessibleBoards]
  );

  const { allStatusesSorted, getStatusByIdWithFallback, getStatusColorById } =
    useTaskStatuses({
      entityId,
      boardId: boardId || null,
    });

  const statusesAsListItems = useMemo<ListBoxItem<TaskCustomStatusDto>[]>(
    () => allStatusesSorted.map((status) => ({ id: status.id, value: status })),
    [allStatusesSorted]
  );

  const board = boardId ? getBoardByIdFromCache(boardId) : undefined;
  const status = statusId ? getStatusByIdWithFallback(statusId) : undefined;

  const isDefaultValuesSet = useRef(false);
  useEffect(() => {
    if (whatsappTemplate && !isDefaultValuesSet.current) {
      isDefaultValuesSet.current = true;
      setValue("name", whatsappTemplate.name);
      setValue("board_id", whatsappTemplate.board_id);
      setValue("action", whatsappTemplate.action);
      setValue("header", whatsappTemplate.header);
      setValue("bodyPlain", whatsappTemplate.body);
      setValue("footer", whatsappTemplate.footer);
    }
  }, [whatsappTemplate, setValue]);
  useEffect(() => {
    if (whatsappTemplate) {
      const rawBody = whatsappBodyToRawBody(whatsappTemplate).replace(
        /{{[\s\S]*?}}| /g,
        (m) => (m === " " ? "&#32;" : m)
      );
      richTextareaRef.current?.readFromWhatsAppMarkdown(rawBody, {
        supportedVariables,
      });
    }
  }, [supportedVariables, getValues, whatsappTemplate]);

  useEffect(() => {
    return registerDirtySource("whatsapp-template-form", isDirty);
  }, [isDirty, registerDirtySource]);

  return (
    <div className="h-full overflow-y-auto bg-grey-100 p-8">
      <div className="flex w-full items-start justify-center gap-4 pr-12">
        <PlainButton
          className={cx(
            collapsedIconButtonClassName,
            "h-8 w-8",
            (isLoading || isUpdateWhatsappTemplateLoading) && "cursor-default"
          )}
          disabled={isLoading || isUpdateWhatsappTemplateLoading}
          onClick={handleSafeBack}
        >
          <ArrowLeftIcon />
        </PlainButton>

        <div className="w-full max-w-300 rounded-2xl border border-solid border-grey-300 bg-white p-8">
          <DataLoadingWrapper
            isLoading={isWhatsappTemplateLoading}
            isFetching={isWhatsappTemplateFetching}
            isError={isWhatsappTemplateError}
            onRetry={refetchWhatsappTemplate}
          >
            <div className="flex w-full items-start justify-between gap-8">
              <div className="flex min-w-0 flex-1 flex-col gap-10">
                <div className="flex gap-4">
                  <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
                  <div className="flex flex-col gap-1">
                    <span className="font-secondary font-medium text-dark">
                      {t({
                        id: "client-support-page-page.whatsapp-template",
                        defaultMessage: "WhatsApp Template",
                      })}
                    </span>
                    <span className="font-secondary text-[13px] leading-[150%] text-dark-700">
                      {isMessageTemplate
                        ? t({
                            id: "client-support-page-page.message-template-description",
                            defaultMessage:
                              "Message Templates can be manually sent to the Customer when chat is expired",
                          })
                        : t({
                            id: "client-support-page-page.task-template-description",
                            defaultMessage:
                              "Task Templates are sent automatically to the Customer specified in the Task when the Order status changes",
                          })}
                    </span>
                  </div>
                </div>

                <Controller
                  control={control}
                  name="name"
                  rules={{
                    required: true,
                    maxLength: 60,
                  }}
                  disabled={!!templateId}
                  render={({ field: { onChange, ...props } }) => {
                    const handleChange = (
                      e: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const input = e.target;
                      const cursorPosition = input.selectionStart;
                      const newValue = e.target.value
                        .trimStart()
                        .toLowerCase()
                        .replace(/\s+/g, "_");

                      setValue("name", newValue, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });

                      requestAnimationFrame(() => {
                        if (input.setSelectionRange) {
                          const newCursorPosition = Math.min(
                            cursorPosition || 0,
                            newValue.length
                          );
                          input.setSelectionRange(
                            newCursorPosition,
                            newCursorPosition
                          );
                        }
                      });
                    };

                    return (
                      <TextInput
                        labelPosition="border-overlap"
                        label={t({
                          id: "client-support-page-page.template-name",
                          defaultMessage: "Template Name",
                        })}
                        placeholder={t({
                          id: "common.enter",
                          defaultMessage: "Enter",
                        })}
                        isRequired
                        lengthIndicator={{ max: 60 }}
                        isInvalid={!!errors.name}
                        autoComplete="off"
                        isDisabled={!!templateId}
                        {...props}
                        onChange={handleChange}
                      />
                    );
                  }}
                />

                {templateCategory === WhatsappTemplateCategory.tasks && (
                  <div className="flex flex-col gap-4">
                    <div className="flex items-start gap-10">
                      <FormGroup
                        isRequired
                        labelPosition="border-overlap"
                        label={t({
                          id: "client-support-page-page.board",
                          defaultMessage: "Board",
                        })}
                        classNames={{ wrapperClassName: "w-1/2" }}
                      >
                        <Controller
                          name="board_id"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ResourcePickerPopover
                              adjustToTriggerWidth
                              placement="bottom-end"
                              items={boardsAsListItems}
                              hasSearch
                              selectionBehavior={{
                                mode: "single",
                                canToggle: false,
                              }}
                              maxVisibleItems={6}
                              defaultSelectedIds={value ? [value] : undefined}
                              itemSize="sm"
                              spaceBetweenItems="compact"
                              renderTrigger={({
                                Trigger,
                                triggerRef,
                                isOpen,
                              }) => (
                                <Trigger
                                  ref={triggerRef}
                                  as={SelectTrigger}
                                  label={
                                    board ? (
                                      <div className="flex items-center gap-1.5">
                                        <BoardAvatar
                                          size="md"
                                          name={board.name}
                                          color={board.color}
                                        />
                                        {board.name}
                                      </div>
                                    ) : (
                                      t({
                                        id: "common.select",
                                        defaultMessage: "Select",
                                      })
                                    )
                                  }
                                  hasValue={!!board}
                                  isOpen={isOpen}
                                  className="h-14"
                                />
                              )}
                              renderLabel={(item) => item.value.name}
                              renderStartIcon={(item) => (
                                <BoardAvatar
                                  size="md"
                                  name={item.value.name}
                                  color={item.value.color}
                                />
                              )}
                              onSelect={({ item, onClose }) => {
                                onChange(item.id);
                                setValue("action", TaskDefaultStatusId.pending);
                                richTextareaRef.current?.clear();
                                setValue("bodyPlain", "");
                                setValue("bodyHtml", "");
                                onClose();
                              }}
                            />
                          )}
                        />
                      </FormGroup>
                      <FormGroup
                        isRequired
                        labelPosition="border-overlap"
                        label={t({
                          id: "client-support-page-page.when-status-set-to",
                          defaultMessage: "When Status set to",
                        })}
                        classNames={{ wrapperClassName: "w-1/2" }}
                      >
                        <Controller
                          name="action"
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <ResourcePickerPopover
                              adjustToTriggerWidth
                              placement="bottom-end"
                              items={statusesAsListItems}
                              hasSearch
                              shouldFilterOnSearch
                              selectionBehavior={{
                                mode: "single",
                                canToggle: false,
                              }}
                              maxVisibleItems={6}
                              defaultSelectedIds={value ? [value] : undefined}
                              itemSize="sm"
                              spaceBetweenItems="compact"
                              renderTrigger={({
                                Trigger,
                                triggerRef,
                                isOpen,
                              }) => (
                                <Trigger
                                  ref={triggerRef}
                                  as={SelectTrigger}
                                  label={
                                    board
                                      ? status
                                        ? status.text
                                        : t({
                                            id: "common.select",
                                            defaultMessage: "Select",
                                          })
                                      : status
                                        ? status.text
                                        : t({
                                            id: "client-support-page-page.select-board-first",
                                            defaultMessage:
                                              "Select Board first",
                                          })
                                  }
                                  hasValue={!!status}
                                  isDisabled={!board}
                                  isOpen={isOpen}
                                  className="h-14"
                                />
                              )}
                              renderLabel={(item) => item.value.text}
                              renderStartIcon={(item) => (
                                <DotIcon
                                  color={getStatusColorById(item.value.id)}
                                />
                              )}
                              onSelect={({ item, onClose }) => {
                                onChange(item.id);
                                onClose();
                              }}
                            />
                          )}
                        />
                      </FormGroup>
                    </div>
                    <div className="border-0 border-l border-solid border-grey-400 pl-2.5 font-secondary text-sm leading-5.25 text-dark-700">
                      {boardId
                        ? t(
                            {
                              id: "client-support-page-page.trigger-description",
                              defaultMessage:
                                "When the Order (Task) Status is set to “{statusName}“ – WhatsApp template will be sent to the Customer specified in the Task details.",
                            },
                            { statusName: status?.text }
                          )
                        : t(
                            {
                              id: "client-support-page-page.trigger-description-no-board",
                              defaultMessage:
                                "When a Status is assigned to “{statusName}“ – WhatsApp template will be sent to the Customer specified in the Task details. To select the Trigger Status, please choose the Board first.",
                            },
                            { statusName: status?.text }
                          )}
                    </div>
                  </div>
                )}

                <div className="h-px w-full rounded-[1px] bg-[#F2F2F2]" />

                <Controller
                  control={control}
                  name="header"
                  rules={{
                    maxLength: 60,
                  }}
                  render={({ field: { onChange, ...props } }) => (
                    <TextInput
                      labelPosition="border-overlap"
                      label={t({
                        id: "client-support-page-page.header",
                        defaultMessage: "Header",
                      })}
                      placeholder={t({
                        id: "common.enter",
                        defaultMessage: "Enter",
                      })}
                      lengthIndicator={{ max: 60 }}
                      isInvalid={!!errors.header}
                      onChange={(e) => onChange(e.target.value.trimStart())}
                      {...props}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="bodyPlain"
                  rules={{
                    required: true,
                    maxLength: 1024,
                  }}
                  render={({ field: { ref } }) => (
                    <FormGroup
                      label={t({
                        id: "client-support-page-page.body",
                        defaultMessage: "Body",
                      })}
                      labelPosition="border-overlap"
                      isRequired
                      isInvalid={!!errors.bodyPlain}
                      errorMessage={errors.bodyPlain?.message}
                      lengthIndicator={{
                        max: 1024,
                        current: bodyPlain.length,
                      }}
                      classNames={{
                        labelBackgroundClassName: "z-1",
                      }}
                    >
                      <RichTextarea
                        ref={assignRefs([ref, richTextareaRef])}
                        namespace="whatsapp-template-editor"
                        placeholder={t({
                          id: "common.enter",
                          defaultMessage: "Enter",
                        })}
                        onEachUpdate={() => {
                          if (!richTextareaRef.current) {
                            return;
                          }

                          const nextBodyPlain =
                            richTextareaRef.current.saveToWhatsAppMarkdown({
                              supportedVariables,
                            });
                          const nextBodyHtml =
                            richTextareaRef.current.saveToHTML();

                          if (nextBodyPlain !== getValues("bodyPlain")) {
                            setValue("bodyPlain", nextBodyPlain, {
                              shouldDirty: true,
                              shouldValidate: isSubmitted,
                            });
                          }

                          if (nextBodyHtml !== getValues("bodyHtml")) {
                            setValue("bodyHtml", nextBodyHtml);
                          }
                        }}
                        classes={{
                          container: cx(
                            "h-[160px] overflow-hidden rounded-[10px] border-[1px] border-solid transition-colors",
                            "focus-within:border-transparent focus-within:ring-2 focus-within:outline-none",
                            errors.bodyPlain
                              ? "border-gradients-danger focus-within:border-gradients-danger focus-within:ring-gradients-danger hover:border-gradients-danger"
                              : "border-grey-400 focus-within:border-[#90C9F9] focus-within:ring-[#90C9F9]"
                          ),
                          contentEditable:
                            "h-full overflow-y-auto px-5 py-4 font-secondary text-base leading-4 outline-none",
                          placeholder:
                            "top-4 left-5 font-secondary text-base text-dark-600",
                        }}
                        endSlot={
                          <WhatsAppTemplateEditorToolbar
                            entityId={entityId}
                            boardId={boardId}
                            hideVariables={isMessageTemplate}
                          />
                        }
                      />
                    </FormGroup>
                  )}
                />
                <Controller
                  control={control}
                  name="footer"
                  rules={{
                    maxLength: 60,
                  }}
                  render={({ field: { onChange, ...props } }) => (
                    <TextInput
                      labelPosition="border-overlap"
                      label={t({
                        id: "client-support-page-page.footer",
                        defaultMessage: "Footer",
                      })}
                      placeholder={t({
                        id: "common.enter",
                        defaultMessage: "Enter",
                      })}
                      lengthIndicator={{ max: 60 }}
                      isInvalid={!!errors.footer}
                      onChange={(e) => onChange(e.target.value.trimStart())}
                      {...props}
                    />
                  )}
                />

                <Button
                  variant="primary"
                  className="w-70"
                  iconEnd={<ArrowRightIcon />}
                  onClick={handleSubmit(handleCreateTemplate)}
                  isDisabled={
                    isLoading ||
                    isUpdateWhatsappTemplateLoading ||
                    (!!templateId && !isDirty)
                  }
                  isLoading={isLoading || isUpdateWhatsappTemplateLoading}
                >
                  {t({
                    id: "common.save",
                    defaultMessage: "Save",
                  })}
                </Button>
              </div>

              <div className="flex shrink-0 flex-col gap-2">
                <span className="font-secondary text-[13px] leading-[150%] text-dark-700">
                  {t({
                    id: "client-support-page-page.preview",
                    defaultMessage: "Preview",
                  })}
                </span>
                <TemplatePreview
                  bodyPlain={bodyPlain}
                  bodyHtml={bodyHtml}
                  footer={footer}
                  header={header}
                />
              </div>
            </div>
          </DataLoadingWrapper>
        </div>
      </div>
      <AutomationUpdatesDialog
        isOpen={isAutomationUpdatesDialogOpen}
        onRequestClose={() => {
          saveItemToStorage(UPDATES_AUTOMATION_DIALOG_SHOWN, true);
          setIsAutomationUpdatesDialogOpen(false);
        }}
      />
      <Alert
        isOpen={isAlertOpen}
        onRequestClose={() => setIsAlertOpen(false)}
        img={
          <Lottie
            options={{ animationData: alertAnimation }}
            width={70}
            height={70}
          />
        }
        title={t({
          id: "client-support-page-page.status-is-taken",
          defaultMessage: "Status is taken",
        })}
        content={t(
          {
            id: "client-support-page-page.template-status-already-assigned",
            defaultMessage:
              "Status {status} already assigned to a WhatsApp template. You can assign a Status to only one WhatsApp Template at a time",
          },
          {
            status: statusId ? (
              <span className="font-medium text-primary-800">
                {getStatusByIdWithFallback(statusId).text}
              </span>
            ) : null,
          }
        )}
        buttons={[
          {
            text: t({ id: "common.okay", defaultMessage: "Okay" }),
            onClick: () => setIsAlertOpen(false),
          },
        ]}
      />
    </div>
  );
};
